import { http } from '@/lib/axios';
import { unwrap, toApiError, getPage } from '@/lib/unwrap';
import type { ApiEnvelope, Page, SearchParams } from '@/types/api';
import type { Product, ResolvedPrices } from '@/types/product';

export interface ProductListResult {
  items: Product[];
  page?: Page;
}

/** Backend product row (superset of the FE Product) — carries `requiresTimeSlot`. */
type RawProduct = Product & { requiresTimeSlot?: boolean };

/**
 * Normalize a backend product into the FE shape. The backend exposes the
 * slot requirement as `requiresTimeSlot`; the app reads `requiresSlot`. Add-on
 * availability is derived from an embedded `addOns` list when present.
 *
 * `attractionId` + `maxPurchasePerSlot` come stamped by the catalog read
 * (`attachAttractionPurchaseCap`, `-1` = unlimited); they drive the per-slot
 * purchase-cap UX gate. Absent → unlimited (handled by `capOfProduct`).
 */
function normalizeProduct(raw: RawProduct): Product {
  const addOns = Array.isArray(raw.addOns)
    ? raw.addOns.map((a) => ({
        ...a,
        attractionId: a.attractionId ?? raw.attractionId,
        maxPurchasePerSlot: a.maxPurchasePerSlot ?? raw.maxPurchasePerSlot,
      }))
    : [];
  return {
    ...raw,
    requiresSlot: raw.requiresSlot ?? raw.requiresTimeSlot === true,
    hasAddOns: raw.hasAddOns ?? addOns.length > 0,
    attractionId: raw.attractionId,
    maxPurchasePerSlot: raw.maxPurchasePerSlot,
    addOns,
  };
}

export async function getProducts(params?: SearchParams): Promise<ProductListResult> {
  try {
    const res = await http.get<ApiEnvelope<RawProduct[]>>('/product', { params });
    const data = unwrap(res.data);
    const items = (Array.isArray(data) ? data : []).map(normalizeProduct);
    return { items, page: getPage(data) };
  } catch (error) {
    throw toApiError(error);
  }
}

/** POST /product/resolve-prices — slot-aware prices for a visit date. */
export async function resolveProductPrices(
  items: Array<{ productId: string; slotId?: string }>,
  date: string,
): Promise<ResolvedPrices> {
  try {
    const res = await http.post<ApiEnvelope<ResolvedPrices>>('/product/resolve-prices', {
      items,
      date,
    });
    return unwrap(res.data);
  } catch (error) {
    throw toApiError(error);
  }
}
